import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { useCalendlyEventListener, InlineWidget } from 'react-calendly';
import getConfig from 'next/config';
import { useDispatch, useSelector } from 'react-redux';

import { Hero, HeroToast, Spacer, Button, PenlySeo, ContentBoxContainer, ContentBoxText, ContentBoxFigure, PageFAQ, InfoDialog, Loader } from 'components';
import { scrollToPageElement } from 'utils/componentsCommon';
import { lawPageFAQEntries } from 'utils/texts';
import SelectableProducts from 'components/Forms/JuraSelectableProducts';
import { fromTablethoriMediaQuery, fromTabletMediaQuery, mediaQueries } from 'utils/MediaQueriesDeprecated';
import { submitOrder } from 'api/orders';
import { awaitAtLeast } from 'utils/async';
import useAuthentication from 'utils/useAuthentication';
import { getOrderDetailsFromLocalStorage, writeOrderDetailsInLocalStorage } from 'utils/orders';
import { getActiveAffiliateId } from 'utils/affiliates';

export default function Jura() {
    const config = getConfig();
    const router = useRouter();
    const { authenticated, login } = useAuthentication();
    const [title] = useState('Bestil juridiske dokumenter til fordelagtige priser');
    const [description] = useState('');
    const [image] = useState({ url: '/static/gfx/content/penly-content-10.jpg' });
    const [buttonText] = useState('Bestil nu');
    const [show, setShow] = useState('');
    const user = useSelector(state => state.user);

    const calendlyBookingId = useMemo(uuidv4, []);
    const [items, setItems] = useState();
    const [productsSnapshot, setProductsSnapshot] = useState();
    const [order, setOrder] = useState({ });
    const [customer, setCustomer] = useState();
    const seoTitle = '';

    const fromTablethori = mediaQueries(fromTablethoriMediaQuery);
    const fromTablet = mediaQueries(fromTabletMediaQuery);
    const { code } = router.query;
    const vendor = code === '9547' ? 'jura-nord-advokater-aps' :config.publicRuntimeConfig.jura?.vendor || 'TealAdvice';
    const vendorName = vendor === 'jura-nord-advokater-aps' ? 'Jura Nord' : 'Alliance Advokater Hørsholm';
    const meetingLocation = vendor === 'jura-nord-advokater-aps' ? 'online' : 'via telefon';
    const subTitle= `Få uvildig rådgivning og styr på juraen med ${vendorName}. Konkret aftale og rådgivning sker direkte med ${vendorName} ${meetingLocation}.`;
    const calendlyBookingLink = vendor === 'jura-nord-advokater-aps' ? 'https://calendly.com/rct-juranord/book-mode' : config.publicRuntimeConfig.jura?.calendlyBookingLink || 'https://calendly.com/ath88/30min';

    const [affiliateId, setAffiliateId] = useState(code === '9547' ? 'jura-nord-advokater-aps' :'alliance-advokater');
    const dispatch = useDispatch();

    useEffect(() => {

        if (user && authenticated) {
            const id = getActiveAffiliateId(user);
            setAffiliateId(id);
        }
    }, [user, authenticated, dispatch]);

    useEffect(() => {
        const foundOrder = getOrderDetailsFromLocalStorage();
        if (foundOrder) setOrder(foundOrder);
    }, []);

    useCalendlyEventListener({ onEventScheduled: async (event) => {
        const orderDetails = { vendor, items, calendlyBookingId, productsSnapshot, discountCode: code, bookingEventId: extractIdFromUri(event.data.payload.event.uri), sendEmail: true };
        writeOrderDetailsInLocalStorage(orderDetails);
        setShow('receipt');
        onSubmitOrder(orderDetails);
    } });

    const handleOrder = (products, productList, cust) => {
        setCustomer(cust);
        setItems(products.map(product => ({ product, productId: product.productId, quantity: 1 })));
        setProductsSnapshot(productList);
        setShow('dialog');
    };

    const handleCloseModal = () => {
        setOrder({ status: undefined });
        setShow();

    };
    const goToCreateProfile = () => router.push(`/opret?destination=/mit-penly/ordrer/${order.orderId}&email=${encodeURIComponent(customer?.email)}&firstName=${encodeURIComponent(customer?.firstName)}&lastName=${encodeURIComponent(customer?.lastName)}`);
    const goToLogin = () => login({ redirectPath: `/mit-penly/ordrer/${order.orderId}` });
    const goToOrderPage = () => router.push(`/mit-penly/ordrer/${order.orderId}`);

    const onSubmitOrder = useCallback(async (orderDetails) => {
        setOrder({ status: 'submitting' });
        setShow('');
        if(!orderDetails.customer){
            orderDetails.customer = customer;
        }
        const createdOrder = await awaitAtLeast(3000, () => submitOrder(orderDetails));
        setOrder(createdOrder);
        setShow('receipt');
    }, [vendor, calendlyBookingId, items]);

    const calendlyLink = `${calendlyBookingLink}?utm_term=penly-booking-id-${calendlyBookingId}`;

    return (
        <>
            <PenlySeo title={ seoTitle } description={ description } image={ image } noindex={ !!code } />
            <Hero full={ true } overlay={ true } figure={ image.url }>
                <section className="title">
                    <h1>{ title }</h1>
                    <h4 className="lowerCase">{ subTitle }</h4>
                    { description && <p>{ description }</p> }
                    <Spacer />
                    <Button onClick={ () => scrollToPageElement('#products') } size={ 'large' }>
                        { buttonText }
                    </Button>
                    <HeroToast className="accentMode" accentColor={ 'var(--color-primary)' } style ={{ fontSize: 'large' }}>
                        <strong>Få uvildig juridisk rådgivning gennem Penly.</strong>
                    </HeroToast>
                </section>
            </Hero>

            { fromTablethori && <Spacer size="medium" /> }

            <div id='products'/>
            <p>Bemærk, at alle møder foregår { meetingLocation }.</p>
            <SelectableProducts affiliateId={ affiliateId } handleOrder={ handleOrder } discountCode={ code } />

            { fromTablethori && <Spacer size="medium" /> }

            <ContentBoxContainer>
                <ContentBoxFigure figure="/static/gfx/content/penly-content-5.jpg" className="brtl servicelogoinline colorWhite">
                </ContentBoxFigure>
                <ContentBoxText className="brbr background-primary" style={{ color: 'white' }}>
                    <h3>Sådan foregår det</h3>
                    <ol className="large">
                        <li>Vælg hvilke ydelser, du er interesseret i tryk &quot;Bestil produkt&quot;.</li>
                        <li>Bestil en tid i kalenderen, hvor det passer dig at holde møde med advokaten.</li>
                        <li>Opret en gratis Penly-profil og del informationer med advokaten forud for jeres møde nemt og sikkert.</li>
                        <li> Få overblik og tryghed på et telefonmødet med advokaten. </li>
                    </ol>
                    <Button onClick={ () => scrollToPageElement('#products') } color="white" size={ fromTablet ? 'large' : 'medium' }>
                        { buttonText }
                    </Button>
                </ContentBoxText>
            </ContentBoxContainer>

            { fromTablethori && <Spacer size="large" /> }

            <PageFAQ answerMarginTop={ -40 } title="FAQ om arv testamente og fremtidsfuldmagt" faqEntries={ lawPageFAQEntries } />

            <ContentBoxContainer className="single" style={{ justifyContent: 'center', marginBottom: '100px' }}>
                <ContentBoxText className="brbr background-primary" style={{ color: 'white' }}>
                    <h3>Har du spørgsmål?</h3>
                    <p>
                        Send en e-mail til <a href={ 'mailto:kontakt@penly.dk' }> kontakt@penly.dk </a>
                        eller ring til os på <a href={ 'tel:+45 93 83 83 72' }> +45 93 83 83 72</a>.
                        Du kan ringe til os tirsdag og torsdag mellem 10.00 og 14.00.
                    </p>
                </ContentBoxText>
            </ContentBoxContainer>

            { show && (
                <InfoDialog onClose={ handleCloseModal } modal={ true } modalContent={
                    <>
                        { show === 'dialog' && (
                            <div>
                                <div style={{ paddingLeft: '50px' }}>
                                    <h4>Mødebooking</h4>
                                </div>
                                <InlineWidget
                                    styles={ fromTablethori ? { width: '1000px', height: '680px' }: { width: '320px', height: '580px' } }
                                    url={ calendlyLink }
                                    prefill={{ email: user?.email || customer?.email, name: user?.name || `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}` }}
                                />
                            </div>
                        ) }
                        { show === 'receipt' && (
                            <>
                                { !authenticated && (!order?.customer?.userId || order?.customer?.enabled === false) && (
                                    <>
                                        <h4>Tak for din bestilling</h4>
                                        <strong>Husk at dele dine oplysninger med { vendorName }</strong>
                                        <p>
                                            For at { vendorName } kan give dig den bedste rådgivning, skal du svare på en række spørgsmål.
                                        </p>
                                        <p>
                                            Opret en gratis profil på Penly og del dine svar nemt og sikkert.
                                            Det tager ca. 10 minutter. Har du ikke tid lige nu? Intet problem. Vi husker dig på det inden mødet.
                                        </p>
                                        <br/>
                                        <Button onClick={ goToCreateProfile }>Opret en gratis profil</Button>
                                    </>
                                ) }

                                { !authenticated && (order?.customer?.userId && order?.customer?.enabled !== false) && (
                                    <>
                                        <h4>Tak for din bestilling</h4>
                                        <strong>Husk at dele dine oplysninger med { vendorName }</strong>
                                        <p>
                                            For at { vendorName } kan give dig den bedste rådgivning, skal du svare på en række spørgsmål.
                                        </p>
                                        <p>
                                            Log ind og del dine svar nemt og sikkert. Det tager ca. 10 minutter.
                                            Har du ikke tid lige nu? Intet problem. Vi husker dig på det inden mødet.
                                        </p>
                                        <br/>
                                        <Button onClick={ goToLogin }>Log ind</Button>
                                    </>
                                ) }

                                { authenticated && (
                                    <>
                                        <h4>Tak for din bestilling</h4>
                                        <strong>Husk at dele dine oplysninger med { vendorName }</strong>
                                        <p>
                                            For at { vendorName } kan give dig den bedste rådgivning, skal
                                            du svare på en række spørgsmål.
                                        </p>
                                        <p>
                                            Del dine svar nemt og sikkert via Penly. Det tager ca. 10 minutter.
                                            Har du ikke tid lige nu? Intet problem. Vi husker dig på det inden mødet.
                                        </p>
                                        <br/>
                                        <Button onClick={ goToOrderPage }>Gå til spørgsmål</Button>
                                    </>
                                ) }
                            </>
                        ) }
                    </>
                } />
            ) }

            <Loader show={ order?.status === 'submitting' } waitText='Vent venligst mens vi færdiggør din bestilling' />
        </>
    );

}

function extractIdFromUri(uri) {
    const uriParts = uri.split('/');
    const id = uriParts[uriParts.length - 1];
    return id;
}
